// v-tip-scope: hover tooltips for long lists without a tooltip instance per item.
//
// Put the directive on a container and `data-tip="…"` on any element inside it.
// One delegated listener per container and a single shared tooltip element on
// <body> replace v-tooltip's per-element Popper, which is built for every row on
// insert and stalls lists with hundreds of rows. No Vue state is touched on
// hover, so the host component never re-renders while the pointer moves.
//
//   <div v-tip-scope>
//     <button data-tip="Remove Session">…</button>
//   </div>

const GAP = 8
const EDGE = 4

let tipEl = null
let contentEl = null
let arrowEl = null
let activeTarget = null

const ensureTip = () => {
  if (tipEl) {
    return tipEl
  }

  tipEl = document.createElement('div')
  tipEl.setAttribute('role', 'tooltip')
  // Matches the slate look of the styled v-tooltip; z-index clears c-modal.
  Object.assign(tipEl.style, {
    position: 'fixed',
    zIndex: '100000',
    display: 'none',
    maxWidth: '280px',
    padding: '6px 9px',
    background: '#2b3a4a',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '1.4',
    textAlign: 'center',
    borderRadius: '6px',
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.22)',
    pointerEvents: 'none',
  })

  contentEl = document.createElement('div')
  arrowEl = document.createElement('div')
  Object.assign(arrowEl.style, {
    position: 'absolute',
    width: '0',
    height: '0',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
  })

  tipEl.appendChild(contentEl)
  tipEl.appendChild(arrowEl)
  document.body.appendChild(tipEl)

  return tipEl
}

const hide = () => {
  activeTarget = null
  if (tipEl) {
    tipEl.style.display = 'none'
  }
}

const show = (target) => {
  const text = target.getAttribute('data-tip')
  if (!text) {
    hide()
    return
  }

  activeTarget = target
  const tip = ensureTip()
  contentEl.textContent = text
  tip.style.display = 'block'

  const rect = target.getBoundingClientRect()
  const tipRect = tip.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const left = Math.min(Math.max(centerX - tipRect.width / 2, EDGE), window.innerWidth - tipRect.width - EDGE)
  // Prefer above the element; flip below when there's no room.
  const placeAbove = rect.top - tipRect.height - GAP >= EDGE

  tip.style.left = `${left}px`
  tip.style.top = `${placeAbove ? rect.top - tipRect.height - GAP : rect.bottom + GAP}px`

  arrowEl.style.left = `${Math.min(Math.max(centerX - left - 5, 6), tipRect.width - 16)}px`
  arrowEl.style.top = placeAbove ? '100%' : ''
  arrowEl.style.bottom = placeAbove ? '' : '100%'
  arrowEl.style.borderTop = placeAbove ? '5px solid #2b3a4a' : '0'
  arrowEl.style.borderBottom = placeAbove ? '0' : '5px solid #2b3a4a'
}

export default {
  bind(el) {
    el.__tipScope = {
      over(event) {
        const target = event.target.closest ? event.target.closest('[data-tip]') : null
        if (!target || !el.contains(target)) {
          if (activeTarget && el.contains(activeTarget)) {
            hide()
          }
          return
        }
        if (target !== activeTarget) {
          show(target)
        }
      },
      leave() {
        // A re-render may have detached the hovered node; drop its tip too.
        if (activeTarget && (!activeTarget.isConnected || el.contains(activeTarget))) {
          hide()
        }
      },
    }

    el.addEventListener('mouseover', el.__tipScope.over)
    el.addEventListener('mouseleave', el.__tipScope.leave)
    // A fixed tip would drift once its list scrolls; a click usually opens a
    // confirm/modal the tip shouldn't sit on top of.
    el.addEventListener('scroll', el.__tipScope.leave, true)
    el.addEventListener('click', el.__tipScope.leave)
  },
  unbind(el) {
    el.__tipScope.leave()
    el.removeEventListener('mouseover', el.__tipScope.over)
    el.removeEventListener('mouseleave', el.__tipScope.leave)
    el.removeEventListener('scroll', el.__tipScope.leave, true)
    el.removeEventListener('click', el.__tipScope.leave)
    delete el.__tipScope
  },
}
